import {
  styled,
  useTheme,
  Theme,
  CSSObject,
  alpha,
} from "@mui/material/styles";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useMediaQuery,
  IconButton,
  Divider,
  Typography,
  Box,
} from "@mui/material";
import React, { Dispatch, SetStateAction, useCallback, useEffect } from "react";
import SpaceDashboardIcon from "@mui/icons-material/SpaceDashboard";
import DynamicFormOutlinedIcon from "@mui/icons-material/DynamicFormOutlined";
import DisplaySettingsIcon from "@mui/icons-material/DisplaySettings";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import { useNavigate, useLocation } from "react-router-dom";
import MuiDrawer from "@mui/material/Drawer";
import { adminPages } from "../../../constants/Constants";
import { capitalizeFirstLetter } from "../../../utils/Utility";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import LockClockOutlinedIcon from "@mui/icons-material/LockClockOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import VoiceChatIcon from "@mui/icons-material/VoiceChat";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import AddToHomeScreenOutlinedIcon from "@mui/icons-material/AddToHomeScreenOutlined";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import HttpsOutlinedIcon from "@mui/icons-material/HttpsOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import LockPersonOutlinedIcon from "@mui/icons-material/LockPersonOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import BarChartIcon from "@mui/icons-material/BarChart";
import SettingsIcon from "@mui/icons-material/Settings";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import SecurityIcon from "@mui/icons-material/Security";
import NavGroup from "./NavGroup";
import NavbarStyles from "./NavbarStyles";

interface IProps {
  drawerWidth: number;
  open: boolean;
  setPageName: Dispatch<SetStateAction<string>>;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

const Navbar: React.FC<IProps> = ({
  drawerWidth,
  open,
  setPageName,
  setOpen,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width:767px)");

  const [expandedGroups, setExpandedGroups] = React.useState<
    Record<string, boolean>
  >({
    Reports: false,
    "UX Configs": false,
    "Feature Configurations": false,
  });

  const openedMixin = (theme: Theme): CSSObject => ({
    width: isMobile ? "100%" : drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
  });

  const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
    width: isMobile ? 0 : `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up("sm")]: {
      width: `calc(${theme.spacing(8)} + 1px)`,
    },
  });

  const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== "open",
  })(({ theme, open }) => ({
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    ...(open && {
      ...openedMixin(theme),
      "& .MuiDrawer-paper": {
        ...openedMixin(theme),
        width: isMobile ? "100%" : drawerWidth,
      },
    }),
    ...(!open && {
      ...closedMixin(theme),
      "& .MuiDrawer-paper": closedMixin(theme),
    }),
  }));

  const DrawerHeader = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
  }));

  const getIcon = useCallback((pageName: string) => {
    switch (pageName) {
      case "dashboard":
        return <SpaceDashboardIcon fontSize="small" />;
      case "forms":
        return <DynamicFormOutlinedIcon fontSize="small" />;
      case "lookup":
        return <StorageRoundedIcon fontSize="small" />;
      case "uiconfiguration":
        return <DisplaySettingsIcon fontSize="small" />;
      case "features":
        return <CategoryOutlinedIcon fontSize="small" />;
      case "secretkeys":
        return <VpnKeyOutlinedIcon fontSize="small" />;
      case "usermanagement":
        return <ManageAccountsOutlinedIcon />;
      case "settings":
        return <SettingsOutlinedIcon fontSize="small" />;
      case "loginreports":
        return <LockClockOutlinedIcon fontSize="small" />;
      case "cms":
        return <ArticleOutlinedIcon fontSize="small" />;
      case "conversation-analysis":
        return <VoiceChatIcon fontSize="small" />;
      case "insure-assist-performance":
        return <InsertChartOutlinedIcon fontSize="small" />;
      case "communication-templates":
        return <AddToHomeScreenOutlinedIcon fontSize="small" />;
      case "prompt-management":
        return <ChatBubbleOutlineIcon fontSize="small" />;
      case "password-configuration":
        return <HttpsOutlinedIcon fontSize="small" />;
      case "roles-permissions":
        return <LockPersonOutlinedIcon fontSize="small" />;
      case "customer-analytics":
        return <InsightsOutlinedIcon fontSize="small" />;
    }
  }, []);

  const handleNavigate = useCallback(
    (pageName: string) => {
      setPageName(pageName);
      navigate(`/admin/${pageName.toLocaleLowerCase()}`);
      if (isMobile) {
        setOpen(false);
      }
    },
    [navigate, setPageName, isMobile, setOpen]
  );

  const getNavItemName = (route: string) => {
    switch (route) {
      case "dashboard":
        return "Dashboard";
      case "forms":
        return "Forms";
      case "lookup":
        return "Lookup";
      case "uiconfiguration":
        return "UI Configuration";
      case "features":
        return "Features";
      case "secretkeys":
        return "Secret Keys";
      case "usermanagement":
        return "User Management";
      case "settings":
        return "Settings";
      case "loginreports":
        return "Login Reports";
      case "cms":
        return "Content Management";
      case "conversation-analysis":
        return "Conversation Analysis";
      case "insure-assist-performance":
        return "Insure Assist Performance";
      case "communication-templates":
        return "Communication Templates";
      case "prompt-management":
        return "Prompt Management";
      case "password-configuration":
        return "Password Rules Configuration";
      case "roles-permissions":
        return "Roles and Permissions";
      case "customer-analytics":
        return "Customer Activity Analytics";
      default:
        break;
    }
    return capitalizeFirstLetter(route);
  };

  const NAV_GROUPS = [
    {
      label: "Reports",
      icon: (
        <BarChartIcon fontSize="small" sx={{ color: "rgba(0, 0, 0, 0.87)" }} />
      ),
      items: [
        { key: "loginreports", name: "Login Reports" },
        { key: "conversation-analysis", name: "Conversation Analysis" },
        { key: "insure-assist-performance", name: "Insure Assist Performance" },
        { key: "customer-analytics", name: "Customer Activity Analytics" },
      ],
    },
    {
      label: "UX Configs",
      icon: (
        <SecurityIcon fontSize="small" sx={{ color: "rgba(0, 0, 0, 0.87)" }} />
      ),
      items: [
        { key: "forms", name: "Forms" },
        { key: "lookup", name: "Lookup" },
        { key: "uiconfiguration", name: "UI Configuration" },
        { key: "cms", name: "Content Management" },
        { key: "communication-templates", name: "Communication Templates" },
      ],
    },
    {
      label: "Feature Configurations",
      icon: (
        <StarOutlineIcon
          fontSize="small"
          sx={{ color: "rgba(0, 0, 0, 0.87)" }}
        />
      ),
      items: [
        { key: "features", name: "Features" },
        { key: "secretkeys", name: "Secret Keys" },
        { key: "usermanagement", name: "User Management" },
        { key: "prompt-management", name: "Prompt Management" },
        { key: "password-configuration", name: "Password Rules Configuration" },
        { key: "roles-permissions", name: "Roles and Permissions" },
      ],
    },
  ];

  const handleGroupClick = (label: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  // Check if any item in a group is currently active
  const isGroupActive = (group: (typeof NAV_GROUPS)[0]) => {
    return group.items.some(
      ({ key }) => location.pathname === `/admin/${key.toLowerCase()}`
    );
  };

  // Sync expandedGroups with openGroups when navbar state changes
  useEffect(() => {
    if (!open) {
      // When navbar closes, copy the openGroups state to expandedGroups
      // setExpandedGroups(openGroups); // This line is removed as per the edit hint
    }
  }, [open]); // Removed openGroups from dependency array

  useEffect(() => {
    // Extract the nav key from the path, e.g. "/admin/forms" => "forms"
    const match = location.pathname.match(/^\/admin\/([^/]+)/);
    if (!match) return;
    const navKey = match[1];

    // If on dashboard, collapse all groups
    if (navKey.toLowerCase() === "dashboard") {
      setExpandedGroups((prev) =>
        Object.fromEntries(Object.keys(prev).map((label) => [label, false]))
      );
      return;
    }

    // Find which group contains this key
    const group = NAV_GROUPS.find((g) =>
      g.items.some((item) => item.key.toLowerCase() === navKey.toLowerCase())
    );
    if (group) {
      setExpandedGroups((prev) => ({
        ...Object.fromEntries(Object.keys(prev).map((label) => [label, false])), // collapse all
        [group.label]: true, // expand the matched group
      }));
    }
  }, [location.pathname]);

  return (
    <Drawer
      variant="permanent"
      open={open}
      id="admin_navbar"
      data-test-id="admin_navbar"
      aria-label="Admin Navigation Menu"
    >
      <DrawerHeader
        id="admin_navbar_header"
        data-test-id="admin_navbar_header"
        aria-label="Admin Navigation Header"
      />
      <List
        id="admin_navbar_list"
        data-test-id="admin_navbar_list"
        aria-label="Admin Navigation List"
      >
        {/* Grouped sections */}
        {NAV_GROUPS.map((group, idx) => (
          <React.Fragment key={group.label}>
            <NavGroup
              open={open}
              groupLabel={group.label}
              groupIcon={group.icon}
              items={group.items}
              expanded={expandedGroups[group.label]}
              onToggleExpand={handleGroupClick}
              onNavigate={handleNavigate}
              getNavItemName={getNavItemName}
              getIcon={getIcon}
            />
            {idx < NAV_GROUPS.length - 1 && (
              <Divider sx={NavbarStyles.divider} />
            )}
          </React.Fragment>
        ))}

        {/* Standalone Settings item */}
        <Divider sx={NavbarStyles.divider} />
        <Tooltip
          title="Settings"
          placement="right-end"
          disableHoverListener={open}
          id="admin_nav_tooltip_settings"
          data-test-id="admin_nav_tooltip_settings"
          aria-label="Settings tooltip"
        >
          <ListItem
            disablePadding
            sx={NavbarStyles.settingsListItem.sx}
            id="admin_nav_item_settings"
            data-test-id="admin_nav_item_settings"
            aria-label="Settings navigation item"
          >
            <ListItemButton
              onClick={() => handleNavigate("settings")}
              sx={NavbarStyles.settingsButton({
                theme,
                open,
                drawerWidth,
                isMobile,
                location,
              })}
              id="admin_nav_button_settings"
              data-test-id="admin_nav_button_settings"
              aria-label="Navigate to Settings"
              aria-current={
                location.pathname === "/admin/settings" ? "page" : undefined
              }
            >
              <ListItemIcon
                sx={NavbarStyles.settingsIcon({
                  theme,
                  open,
                  drawerWidth,
                  isMobile,
                  location,
                })}
                id="admin_nav_icon_settings"
                data-test-id="admin_nav_icon_settings"
                aria-hidden="true"
              >
                {getIcon("settings")}
              </ListItemIcon>
              <ListItemText
                primary={getNavItemName("settings")}
                sx={NavbarStyles.settingsText({
                  theme,
                  open,
                  drawerWidth,
                  isMobile,
                  location,
                })}
                id="admin_nav_text_settings"
                data-test-id="admin_nav_text_settings"
              />
            </ListItemButton>
          </ListItem>
        </Tooltip>
      </List>
    </Drawer>
  );
};

export default Navbar;
